import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { 
  Upload, LineChart, TrendingUp, TrendingDown, Brain, Loader2, 
  ArrowLeft, Camera, BarChart3, Target, DollarSign, AlertTriangle,
  ChevronDown, ChevronUp, RefreshCw, Settings, Zap, Shield, Flame,
  Clock, Activity, Eye, CheckCircle, XCircle, Maximize2, Minimize2, X,
  MessageSquare
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import ReactMarkdown from 'react-markdown';
import { normalizeSymbol } from '../utils/symbolUtils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// TradingView Widget Component
const TradingViewWidget = ({ symbol, interval = "D", height = "500px" }) => {
  const containerRef = useRef(null);
  
  useEffect(() => {
    if (!symbol || !containerRef.current) return;
    
    // Clear previous widget
    containerRef.current.innerHTML = '';
    
    // Create new widget
    const script = document.createElement('script');
    script.src = 'https://s3.tradingview.com/external-embedding/embed-widget-advanced-chart.js';
    script.type = 'text/javascript';
    script.async = true;
    script.innerHTML = JSON.stringify({
      "autosize": true,
      "symbol": symbol,
      "interval": interval,
      "timezone": "America/New_York",
      "theme": "dark",
      "style": "1",
      "locale": "en",
      "enable_publishing": false,
      "backgroundColor": "rgba(15, 23, 42, 1)",
      "gridColor": "rgba(51, 65, 85, 0.3)",
      "hide_top_toolbar": false,
      "hide_legend": false,
      "save_image": true,
      "calendar": false,
      "hide_volume": false,
      "support_host": "https://www.tradingview.com",
      "studies": [
        "RSI@tv-basicstudies",
        "MASimple@tv-basicstudies",
        "MACD@tv-basicstudies"
      ]
    });
    
    containerRef.current.appendChild(script);
    
    return () => {
      if (containerRef.current) {
        containerRef.current.innerHTML = '';
      }
    };
  }, [symbol, interval]);
  
  return (
    <div className="tradingview-widget-container w-full" style={{ height }} ref={containerRef}>
      <div className="tradingview-widget-container__widget h-full w-full"></div>
    </div>
  );
};

// Fullscreen Chart Modal
const FullscreenChart = ({ symbol, interval, onClose }) => {
  useEffect(() => {
    const handleEsc = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleEsc);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleEsc);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  return (
    <div 
      className="fixed inset-0 z-50 bg-slate-950/95 flex flex-col"
      data-testid="fullscreen-chart-overlay"
    >
      <div className="flex items-center justify-between px-4 py-2 bg-slate-900 border-b border-slate-700">
        <div className="flex items-center gap-3">
          <BarChart3 className="h-5 w-5 text-indigo-400" />
          <span className="text-white font-semibold text-lg">{symbol} - Live Chart</span>
          <Badge variant="outline" className="text-slate-300 border-slate-600 text-xs">
            Fullscreen
          </Badge>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            className="text-slate-400 hover:text-white"
            onClick={() => window.open(`https://www.tradingview.com/chart/?symbol=${symbol}`, '_blank')}
            data-testid="open-tradingview-btn"
          >
            <Eye className="h-4 w-4 mr-1" /> Open in TradingView
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="text-slate-400 hover:text-white hover:bg-slate-700"
            onClick={onClose}
            data-testid="close-fullscreen-chart-btn"
          >
            <X className="h-5 w-5" />
          </Button>
        </div>
      </div>
      <div className="flex-1 p-2">
        <TradingViewWidget symbol={symbol} interval={interval} height="100%" />
      </div>
      <div className="px-4 py-1.5 bg-slate-900 border-t border-slate-800 text-center">
        <span className="text-xs text-slate-500">Press <kbd className="px-1.5 py-0.5 bg-slate-800 rounded text-slate-400 text-xs">Esc</kbd> to close</span>
      </div>
    </div>
  );
};

// Analysis Result Component with Markdown rendering
const AnalysisResult = ({ analysis, isLoading }) => {
  const [expanded, setExpanded] = useState(true);
  
  if (isLoading) {
    return (
      <Card className="bg-slate-800/50 border-slate-700">
        <CardContent className="p-8 flex flex-col items-center justify-center space-y-4">
          <Loader2 className="w-12 h-12 animate-spin text-indigo-400" />
          <p className="text-slate-300 text-lg">Analyzing chart patterns...</p>
          <p className="text-slate-500 text-sm">This may take 15-30 seconds for comprehensive analysis</p>
        </CardContent>
      </Card>
    );
  }
  
  if (!analysis) return null;
  
  return (
    <Card className="bg-slate-800/50 border-slate-700">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-white">
            <Brain className="w-5 h-5 text-indigo-400" />
            AI Chart Analysis
          </CardTitle>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setExpanded(!expanded)}
            className="text-slate-400"
          >
            {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </Button>
        </div>
      </CardHeader>
      {expanded && (
        <CardContent className="prose prose-invert prose-sm max-w-none">
          <div className="text-slate-300 whitespace-pre-wrap analysis-content">
            <ReactMarkdown
              components={{
                h1: ({node, ...props}) => <h1 className="text-2xl font-bold text-white mt-6 mb-3" {...props} />,
                h2: ({node, ...props}) => <h2 className="text-xl font-semibold text-indigo-400 mt-5 mb-2 border-b border-slate-700 pb-1" {...props} />,
                h3: ({node, ...props}) => <h3 className="text-lg font-medium text-emerald-400 mt-4 mb-2" {...props} />,
                h4: ({node, ...props}) => <h4 className="text-base font-medium text-amber-400 mt-3 mb-1" {...props} />,
                p: ({node, ...props}) => <p className="text-slate-300 my-2 leading-relaxed" {...props} />,
                ul: ({node, ...props}) => <ul className="list-disc list-inside space-y-1 my-2 text-slate-300" {...props} />,
                ol: ({node, ...props}) => <ol className="list-decimal list-inside space-y-1 my-2 text-slate-300" {...props} />,
                li: ({node, ...props}) => <li className="text-slate-300" {...props} />,
                strong: ({node, ...props}) => <strong className="text-white font-semibold" {...props} />,
                em: ({node, ...props}) => <em className="text-indigo-300" {...props} />,
                code: ({node, ...props}) => <code className="bg-slate-700 px-1.5 py-0.5 rounded text-emerald-400 text-sm" {...props} />,
                blockquote: ({node, ...props}) => <blockquote className="border-l-4 border-indigo-500 pl-4 my-3 text-slate-400 italic" {...props} />,
              }}
            >
              {analysis}
            </ReactMarkdown>
          </div>
        </CardContent>
      )}
    </Card>
  );
};

const ChartAnalysis = () => {
  // Tab state
  const [activeTab, setActiveTab] = useState('upload');
  
  // Upload analysis state
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [additionalContext, setAdditionalContext] = useState('');
  const [uploadAnalysis, setUploadAnalysis] = useState(null);
  const [uploadLoading, setUploadLoading] = useState(false);
  
  // TradingView analysis state
  const [symbol, setSymbol] = useState('');
  const [searchSymbol, setSearchSymbol] = useState('');
  const [timeframe, setTimeframe] = useState('D');
  const [selectedIndicators, setSelectedIndicators] = useState(['RSI', 'MACD', 'BB', 'SMA', 'VOL']);
  const [tvAnalysis, setTvAnalysis] = useState(null);
  const [tvLoading, setTvLoading] = useState(false);
  const [technicalData, setTechnicalData] = useState(null);
  const [chartFullscreen, setChartFullscreen] = useState(false);
  
  // Common state
  const [aiModel, setAiModel] = useState('gemini');
  const [availableIndicators, setAvailableIndicators] = useState([]);
  const [availableTimeframes, setAvailableTimeframes] = useState([]);
  
  // File input ref
  const fileInputRef = useRef(null);
  
  // Fetch available indicators and timeframes
  useEffect(() => {
    const fetchIndicators = async () => {
      try {
        const response = await axios.get(`${API}/chart-analysis/indicators`);
        setAvailableIndicators(response.data.indicators);
        setAvailableTimeframes(response.data.timeframes);
      } catch (error) {
        console.error('Error fetching indicators:', error);
      }
    };
    fetchIndicators();
  }, []);
  
  // Handle file selection
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        toast.error('Please select an image file');
        return;
      }
      if (file.size > 10 * 1024 * 1024) { // 10MB limit
        toast.error('File size must be less than 10MB');
        return;
      }
      setSelectedFile(file);
      setPreviewUrl(URL.createObjectURL(file));
      setUploadAnalysis(null);
    }
  };
  
  // Handle drag and drop
  const handleDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file && file.type.startsWith('image/')) {
      setSelectedFile(file);
      setPreviewUrl(URL.createObjectURL(file));
      setUploadAnalysis(null);
    }
  };
  
  // Analyze uploaded chart
  const analyzeUploadedChart = async () => {
    if (!selectedFile) {
      toast.error('Please select a chart image first');
      return;
    }
    
    setUploadLoading(true);
    toast.info('Analyzing chart... This may take 15-30 seconds');
    
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('ai_model', aiModel);
      formData.append('additional_context', additionalContext);
      
      const response = await axios.post(`${API}/chart-analysis/analyze-image`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120000
      });
      
      setUploadAnalysis(response.data.analysis);
      toast.success('Chart analysis complete!');
    } catch (error) {
      console.error('Error analyzing chart:', error);
      toast.error(error.response?.data?.detail || 'Failed to analyze chart');
    } finally {
      setUploadLoading(false);
    }
  };
  
  // Handle TradingView symbol search
  const handleSymbolSearch = () => {
    const normalized = normalizeSymbol(searchSymbol);
    if (normalized) {
      setSymbol(normalized);
      setTvAnalysis(null);
      setTechnicalData(null);
    }
  };
  
  // Analyze TradingView chart
  const analyzeTradingViewChart = async () => {
    if (!symbol) {
      toast.error('Please enter a stock symbol first');
      return;
    }
    
    setTvLoading(true);
    toast.info('Analyzing chart with AI... This may take 15-30 seconds');
    
    try {
      const response = await axios.post(`${API}/chart-analysis/analyze-tradingview`, {
        symbol: symbol,
        timeframe: timeframe,
        indicators: selectedIndicators,
        ai_model: aiModel
      }, { timeout: 120000 });
      
      setTvAnalysis(response.data.analysis);
      setTechnicalData(response.data.technical_data);
      toast.success('Chart analysis complete!');
    } catch (error) {
      console.error('Error analyzing TradingView chart:', error);
      toast.error(error.response?.data?.detail || 'Failed to analyze chart');
    } finally {
      setTvLoading(false);
    }
  };
  
  // Toggle indicator selection
  const toggleIndicator = (indicatorId) => {
    setSelectedIndicators(prev => 
      prev.includes(indicatorId)
        ? prev.filter(id => id !== indicatorId)
        : [...prev, indicatorId]
    );
  };
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-indigo-950">
      {/* Header */}
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-slate-900/80 border-b border-slate-800">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/">
                <Button variant="ghost" size="sm" className="text-slate-400 hover:text-white">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back to Dashboard
                </Button>
              </Link>
              <div className="h-6 w-px bg-slate-700" />
              <div className="flex items-center gap-2">
                <LineChart className="w-6 h-6 text-indigo-400" />
                <h1 className="text-xl font-bold text-white">Chart Analysis</h1>
              </div>
            </div>
            
            {/* AI Model Selector */}
            <div className="flex items-center gap-3">
              <Label className="text-slate-400 text-sm">AI Model:</Label>
              <Select value={aiModel} onValueChange={setAiModel}>
                <SelectTrigger className="w-44 bg-slate-800 border-slate-700 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700">
                  <SelectItem value="gemini" className="text-white">Gemini 2.5 Pro</SelectItem>
                  <SelectItem value="claude" className="text-white">Claude Sonnet 4.5</SelectItem>
                  <SelectItem value="fast" className="text-white">Gemini Flash (Fast)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      </header>
      
      {/* Main Content */}
      <main className="container mx-auto px-4 py-6">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-slate-800/50 border border-slate-700 p-1">
            <TabsTrigger 
              value="upload" 
              className="data-[state=active]:bg-indigo-600 data-[state=active]:text-white"
            >
              <Upload className="w-4 h-4 mr-2" />
              Upload Chart
            </TabsTrigger>
            <TabsTrigger 
              value="tradingview"
              className="data-[state=active]:bg-indigo-600 data-[state=active]:text-white"
            >
              <BarChart3 className="w-4 h-4 mr-2" />
              TradingView Analysis
            </TabsTrigger>
          </TabsList>
          
          {/* Upload Chart Tab */}
          <TabsContent value="upload" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Upload Section */}
              <Card className="bg-slate-800/50 border-slate-700">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-white">
                    <Camera className="w-5 h-5 text-indigo-400" />
                    Upload Chart Screenshot
                  </CardTitle>
                  <CardDescription>
                    Upload a screenshot of any stock chart for AI-powered pattern analysis
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Drop Zone */}
                  <div
                    className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
                      previewUrl 
                        ? 'border-indigo-500 bg-indigo-500/10' 
                        : 'border-slate-600 hover:border-indigo-500 hover:bg-slate-700/30'
                    }`}
                    onClick={() => fileInputRef.current?.click()}
                    onDrop={handleDrop}
                    onDragOver={(e) => e.preventDefault()}
                  >
                    {previewUrl ? (
                      <div className="space-y-3">
                        <img 
                          src={previewUrl} 
                          alt="Chart preview" 
                          className="max-h-64 mx-auto rounded-lg border border-slate-600"
                        />
                        <p className="text-slate-400 text-sm">Click to change image</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        <Upload className="w-12 h-12 mx-auto text-slate-500" />
                        <p className="text-slate-300">Drop chart image here or click to upload</p>
                        <p className="text-slate-500 text-sm">Supports PNG, JPG, WEBP (max 10MB)</p>
                      </div>
                    )}
                  </div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleFileSelect}
                    className="hidden"
                  />
                  
                  {/* Additional Context */}
                  <div className="space-y-2">
                    <Label className="text-slate-300">Additional Context (Optional)</Label>
                    <Textarea
                      placeholder="E.g., 'This is AAPL daily chart, looking for entry points' or 'Identify support/resistance levels'"
                      value={additionalContext}
                      onChange={(e) => setAdditionalContext(e.target.value)}
                      className="bg-slate-900 border-slate-700 text-white placeholder:text-slate-500"
                      rows={3}
                    />
                  </div>
                  
                  {/* Analyze Button */}
                  <Button
                    onClick={analyzeUploadedChart}
                    disabled={!selectedFile || uploadLoading}
                    className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700"
                  >
                    {uploadLoading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Analyzing...
                      </>
                    ) : (
                      <>
                        <Brain className="w-4 h-4 mr-2" />
                        Analyze Chart
                      </>
                    )}
                  </Button>
                </CardContent>
              </Card>
              
              {/* Info Card */}
              <Card className="bg-slate-800/50 border-slate-700">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-white">
                    <Eye className="w-5 h-5 text-emerald-400" />
                    What AI Analyzes
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-3">
                    {[
                      { icon: Activity, label: 'Candlestick Patterns', desc: 'Hammer, Doji, Engulfing, etc.' },
                      { icon: TrendingUp, label: 'Chart Patterns', desc: 'H&S, Triangles, Channels' },
                      { icon: Target, label: 'Support/Resistance', desc: 'Key price levels' },
                      { icon: DollarSign, label: 'Entry/Exit Points', desc: 'Specific price recommendations' },
                      { icon: Shield, label: 'Stop Loss', desc: 'Risk management levels' },
                      { icon: Zap, label: 'Option Strategies', desc: 'Multiple strategies by risk' },
                    ].map((item, idx) => (
                      <div key={idx} className="flex items-start gap-2 p-3 bg-slate-900/50 rounded-lg">
                        <item.icon className="w-5 h-5 text-indigo-400 mt-0.5" />
                        <div>
                          <p className="text-white text-sm font-medium">{item.label}</p>
                          <p className="text-slate-500 text-xs">{item.desc}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                    <div className="flex items-start gap-2">
                      <AlertTriangle className="w-5 h-5 text-amber-400 mt-0.5" />
                      <div>
                        <p className="text-amber-400 font-medium text-sm">Tip for Best Results</p>
                        <p className="text-slate-400 text-xs mt-1">
                          Upload clear screenshots showing candlesticks with visible OHLC data. 
                          Include any visible indicators for better analysis.
                        </p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
            
            {/* Analysis Result */}
            <AnalysisResult analysis={uploadAnalysis} isLoading={uploadLoading} />
          </TabsContent>
          
          {/* TradingView Analysis Tab */}
          <TabsContent value="tradingview" className="space-y-6">
            {/* Controls */}
            <Card className="bg-slate-800/50 border-slate-700">
              <CardContent className="p-4">
                <div className="flex flex-wrap items-end gap-4">
                  {/* Symbol Input */}
                  <div className="flex-1 min-w-[200px] space-y-2">
                    <Label className="text-slate-300">Stock Symbol</Label>
                    <div className="flex gap-2">
                      <Input
                        placeholder="Enter symbol (e.g., AAPL)"
                        value={searchSymbol}
                        onChange={(e) => setSearchSymbol(e.target.value.toUpperCase())}
                        onKeyDown={(e) => e.key === 'Enter' && handleSymbolSearch()}
                        className="bg-slate-900 border-slate-700 text-white placeholder:text-slate-500"
                      />
                      <Button onClick={handleSymbolSearch} variant="secondary">
                        Load
                      </Button>
                    </div>
                  </div>
                  
                  {/* Timeframe Selector */}
                  <div className="space-y-2">
                    <Label className="text-slate-300">Timeframe</Label>
                    <Select value={timeframe} onValueChange={setTimeframe}>
                      <SelectTrigger className="w-36 bg-slate-900 border-slate-700 text-white">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-800 border-slate-700">
                        {availableTimeframes.map(tf => (
                          <SelectItem key={tf.id} value={tf.id} className="text-white">
                            {tf.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {/* Analyze Button */}
                  <div className="flex items-center gap-2">
                    <Button
                      onClick={analyzeTradingViewChart}
                      disabled={!symbol || tvLoading}
                      className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700"
                    >
                      {tvLoading ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Analyzing...
                        </>
                      ) : (
                        <>
                          <Brain className="w-4 h-4 mr-2" />
                          Analyze with AI
                        </>
                      )}
                    </Button>
                    {symbol && (
                      <Button
                        variant="outline"
                        className="border-green-500/40 text-green-400 hover:bg-green-500/20"
                        onClick={() => window.open('https://app.askape.com', '_blank', 'noopener,noreferrer')}
                        data-testid="askape-chart-btn"
                      >
                        <MessageSquare className="w-4 h-4 mr-2" />
                        Ask AskApe
                      </Button>
                    )}
                  </div>
                </div>
                
                {/* Indicator Selection */}
                <div className="mt-4 pt-4 border-t border-slate-700">
                  <Label className="text-slate-300 mb-2 block">Technical Indicators to Analyze</Label>
                  <div className="flex flex-wrap gap-2">
                    {availableIndicators.map(indicator => (
                      <Badge
                        key={indicator.id}
                        variant={selectedIndicators.includes(indicator.id) ? "default" : "outline"}
                        className={`cursor-pointer transition-colors ${
                          selectedIndicators.includes(indicator.id)
                            ? 'bg-indigo-600 hover:bg-indigo-700 text-white'
                            : 'border-slate-600 text-slate-400 hover:border-indigo-500 hover:text-white'
                        }`}
                        onClick={() => toggleIndicator(indicator.id)}
                      >
                        {selectedIndicators.includes(indicator.id) && (
                          <CheckCircle className="w-3 h-3 mr-1" />
                        )}
                        {indicator.name}
                      </Badge>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
            
            {/* TradingView Chart */}
            {symbol && (
              <Card className="bg-slate-800/50 border-slate-700 overflow-hidden">
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="flex items-center gap-2 text-white">
                      <BarChart3 className="w-5 h-5 text-indigo-400" />
                      {symbol} - Live Chart
                    </CardTitle>
                    <Button
                      variant="outline"
                      size="sm"
                      className="border-slate-600 text-slate-300 hover:text-white hover:bg-slate-700"
                      onClick={() => setChartFullscreen(true)}
                      data-testid="expand-chart-btn"
                    >
                      <Maximize2 className="h-4 w-4 mr-1.5" />
                      Expand Chart
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="p-0">
                  <TradingViewWidget symbol={symbol} interval={timeframe} />
                </CardContent>
              </Card>
            )}

            {/* Fullscreen Chart Modal */}
            {chartFullscreen && symbol && (
              <FullscreenChart
                symbol={symbol}
                interval={timeframe}
                onClose={() => setChartFullscreen(false)}
              />
            )}
            
            {/* Technical Data Summary */}
            {technicalData && (
              <Card className="bg-slate-800/50 border-slate-700">
                <CardHeader className="pb-2">
                  <CardTitle className="flex items-center gap-2 text-white">
                    <Activity className="w-5 h-5 text-emerald-400" />
                    Technical Indicators Summary
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
                    {technicalData.rsi && (
                      <div className="p-3 bg-slate-900/50 rounded-lg">
                        <p className="text-slate-500 text-xs">RSI (14)</p>
                        <p className={`text-lg font-bold ${
                          technicalData.rsi > 70 ? 'text-red-400' : 
                          technicalData.rsi < 30 ? 'text-emerald-400' : 'text-white'
                        }`}>
                          {technicalData.rsi.toFixed(1)}
                        </p>
                      </div>
                    )}
                    {technicalData.macd !== null && (
                      <div className="p-3 bg-slate-900/50 rounded-lg">
                        <p className="text-slate-500 text-xs">MACD</p>
                        <p className={`text-lg font-bold ${
                          technicalData.macd > 0 ? 'text-emerald-400' : 'text-red-400'
                        }`}>
                          {technicalData.macd.toFixed(3)}
                        </p>
                      </div>
                    )}
                    {technicalData.sma_20 && (
                      <div className="p-3 bg-slate-900/50 rounded-lg">
                        <p className="text-slate-500 text-xs">SMA 20</p>
                        <p className="text-white text-lg font-bold">${technicalData.sma_20.toFixed(2)}</p>
                      </div>
                    )}
                    {technicalData.sma_50 && (
                      <div className="p-3 bg-slate-900/50 rounded-lg">
                        <p className="text-slate-500 text-xs">SMA 50</p>
                        <p className="text-white text-lg font-bold">${technicalData.sma_50.toFixed(2)}</p>
                      </div>
                    )}
                    {technicalData.atr && (
                      <div className="p-3 bg-slate-900/50 rounded-lg">
                        <p className="text-slate-500 text-xs">ATR (14)</p>
                        <p className="text-amber-400 text-lg font-bold">${technicalData.atr.toFixed(2)}</p>
                      </div>
                    )}
                    {technicalData.volume_ratio && (
                      <div className="p-3 bg-slate-900/50 rounded-lg">
                        <p className="text-slate-500 text-xs">Volume</p>
                        <p className={`text-lg font-bold ${
                          technicalData.volume_ratio > 1.5 ? 'text-emerald-400' : 
                          technicalData.volume_ratio < 0.5 ? 'text-red-400' : 'text-white'
                        }`}>
                          {technicalData.volume_ratio.toFixed(1)}x avg
                        </p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}
            
            {/* Analysis Result */}
            <AnalysisResult analysis={tvAnalysis} isLoading={tvLoading} />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default ChartAnalysis;
